if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.imagePreloaders = new Map();
        this.addEventListener('pointerup', this.onModalPointerUpCapture.bind(this), true);
        this.addEventListener('pointerup', this.onPopupPointerUp.bind(this));
        this.addEventListener('click', this.onNavigationClick.bind(this));
        this.addEventListener('click', this.onMediaClick.bind(this));
        this.addEventListener('mousemove', this.onZoomedImageMouseMove.bind(this));
        this.addEventListener('keydown', this.onKeyDown.bind(this));
      }

      get allMediaItems() {
        return Array.from(this.querySelectorAll('.product-media-modal__content > [data-media-id]'));
      }

      get mediaItems() {
        return this.allMediaItems.filter((item) => !item.hidden);
      }

      get navigationMediaItems() {
        const mediaItems = this.mediaItems;
        if (!window.matchMedia('(min-width: 750px)').matches) return mediaItems;

        // When hide_variants is enabled, desktop CSS hides all variant-only modal items except the first slot.
        return mediaItems.filter((item, index) => !(index > 0 && item.classList.contains('product__media-item--variant')));
      }

      suppressGhostClick(pointerEvent) {
        const clickTolerance = 24;
        const pointerX = Number.isFinite(pointerEvent.clientX) ? pointerEvent.clientX : null;
        const pointerY = Number.isFinite(pointerEvent.clientY) ? pointerEvent.clientY : null;

        const shouldBlockEvent = (event) => {
          if (pointerX === null || pointerY === null) return true;
          if (!Number.isFinite(event.clientX) || !Number.isFinite(event.clientY)) return true;
          if (event.clientX === 0 && event.clientY === 0) return true;

          return (
            Math.abs(event.clientX - pointerX) <= clickTolerance &&
            Math.abs(event.clientY - pointerY) <= clickTolerance
          );
        };

        const blockEvent = (event) => {
          if (event.type === 'focusin' && event.target instanceof HTMLElement) {
            event.target.blur();
          }

          if (!shouldBlockEvent(event)) return;

          event.preventDefault();
          event.stopPropagation();
          if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
        };

        ['touchstart', 'touchend', 'mousedown', 'mouseup', 'click'].forEach((eventName) => {
          window.addEventListener(eventName, blockEvent, { capture: true, once: true });
        });
        document.addEventListener('focusin', blockEvent, { capture: true, once: true });
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      showActiveMedia(mediaId = this.openedBy?.getAttribute('data-media-id')) {
        const mediaItems = this.mediaItems;
        if (!mediaItems.length) return;
        const content = this.querySelector('.product-media-modal__content');
        const previousActiveMedia = this.allMediaItems.find((item) => item.classList.contains('active'));

        this.resetImageZoom();

        this.allMediaItems.forEach((element) => element.classList.remove('active'));
        const activeMedia =
          mediaItems.find((element) => element.dataset.mediaId === `${mediaId}`) || mediaItems[0];
        if (!activeMedia) return;

        this.showPendingMediaState(activeMedia);
        this.lockContentHeight(previousActiveMedia, content);

        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        activeMedia.scrollIntoView({ block: 'nearest', inline: 'center' });

        const container = this.querySelector('[role="document"]');
        if (container) {
          const activeMediaWidth = activeMedia.getBoundingClientRect().width || activeMedia.clientWidth || 0;
          container.scrollLeft = Math.max((activeMediaWidth - container.clientWidth) / 2, 0);
        }

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();

        this.preloadNearbyMedia(activeMedia.dataset.mediaId);
        this.releaseContentLocksWhenReady(activeMedia, content);
        this.updateNavigation();
      }

      hide() {
        this.resetImageZoom();
        super.hide();
      }

      showPendingMediaState(activeMedia) {
        if (!this.classList.contains('product-media-modal--popup')) return;
        if (!window.matchMedia('(min-width: 750px)').matches) return;

        const shouldWaitForImage = activeMedia instanceof HTMLImageElement && !(activeMedia.complete && activeMedia.naturalWidth > 0);
        this.classList.toggle('is-waiting-for-media', shouldWaitForImage);
      }

      lockContentHeight(previousActiveMedia, content) {
        if (!content) return;

        const currentHeight =
          previousActiveMedia?.getBoundingClientRect().height || content.getBoundingClientRect().height || 0;

        if (currentHeight > 0) {
          content.style.setProperty('--product-media-modal-lock-height', `${Math.ceil(currentHeight)}px`);
          content.classList.add('is-locked');
        }
      }

      releaseContentLocksWhenReady(activeMedia, content) {
        if (!content) return;

        const unlockContent = () => {
          requestAnimationFrame(() => {
            this.classList.remove('is-waiting-for-media');
            content.classList.remove('is-locked');
            content.style.removeProperty('--product-media-modal-lock-height');
          });
        };

        if (activeMedia instanceof HTMLImageElement) {
          if (activeMedia.complete && activeMedia.naturalWidth > 0) {
            unlockContent();
            return;
          }

          const onImageReady = () => {
            if (typeof activeMedia.decode === 'function') {
              activeMedia.decode().catch(() => {}).finally(unlockContent);
              return;
            }

            unlockContent();
          };

          activeMedia.addEventListener('load', onImageReady, { once: true });
          activeMedia.addEventListener('error', unlockContent, { once: true });
          return;
        }

        this.classList.remove('is-waiting-for-media');
        unlockContent();
      }

      preloadNearbyMedia(mediaId) {
        const mediaItems = this.navigationMediaItems;
        const currentIndex = mediaItems.findIndex((item) => item.dataset.mediaId === `${mediaId}`);
        if (currentIndex < 0) return;

        [0, 1, -1, 2, -2].forEach((offset, orderIndex) => {
          const targetIndex = (currentIndex + offset + mediaItems.length) % mediaItems.length;
          this.preloadMediaItem(mediaItems[targetIndex], orderIndex === 0 ? 'high' : 'auto');
        });
      }

      preloadMediaItem(mediaItem, priority = 'auto') {
        if (!(mediaItem instanceof HTMLImageElement)) return;

        mediaItem.loading = 'eager';
        mediaItem.fetchPriority = priority;

        if (mediaItem.complete && mediaItem.naturalWidth > 0) return;
        if (this.imagePreloaders.has(mediaItem.dataset.mediaId)) return;

        const preloader = new Image();
        preloader.decoding = 'async';
        preloader.fetchPriority = priority;

        if (mediaItem.sizes) preloader.sizes = mediaItem.sizes;
        if (mediaItem.srcset) preloader.srcset = mediaItem.srcset;
        preloader.src = mediaItem.currentSrc || mediaItem.src;

        const cleanup = () => {
          this.imagePreloaders.delete(mediaItem.dataset.mediaId);
        };

        preloader.addEventListener('load', cleanup, { once: true });
        preloader.addEventListener('error', cleanup, { once: true });
        this.imagePreloaders.set(mediaItem.dataset.mediaId, preloader);
      }

      resetImageZoom() {
        const zoomedImage = this.querySelector('.product-media-modal__image.is-zoomed');
        if (!zoomedImage) return;

        zoomedImage.classList.remove('is-zoomed');
        zoomedImage.style.removeProperty('--zoom-origin-x');
        zoomedImage.style.removeProperty('--zoom-origin-y');
      }

      isPopupDesktopMode() {
        return this.classList.contains('product-media-modal--popup') && window.matchMedia('(min-width: 750px)').matches;
      }

      onMediaClick(event) {
        if (!this.isPopupDesktopMode()) return;

        const image = event.target.closest('.product-media-modal__image');
        if (!(image instanceof HTMLImageElement) || !image.classList.contains('active')) return;

        if (!(image.complete && image.naturalWidth > 0)) return;

        event.preventDefault();
        event.stopPropagation();

        const isZoomingIn = !image.classList.contains('is-zoomed');
        this.resetImageZoom();
        if (!isZoomingIn) return;

        this.updateImageZoomOrigin(image, event);
        image.classList.add('is-zoomed');
      }

      onZoomedImageMouseMove(event) {
        if (!this.isPopupDesktopMode()) return;

        const image = event.target.closest('.product-media-modal__image.is-zoomed');
        if (!(image instanceof HTMLImageElement)) return;

        this.updateImageZoomOrigin(image, event);
      }

      updateImageZoomOrigin(image, event) {
        const bounds = image.getBoundingClientRect();
        if (!bounds.width || !bounds.height) return;

        const offsetX = Math.min(Math.max(event.clientX - bounds.left, 0), bounds.width);
        const offsetY = Math.min(Math.max(event.clientY - bounds.top, 0), bounds.height);
        image.style.setProperty('--zoom-origin-x', `${(offsetX / bounds.width) * 100}%`);
        image.style.setProperty('--zoom-origin-y', `${(offsetY / bounds.height) * 100}%`);
      }

      updateNavigation() {
        const hasMultipleMedia = this.navigationMediaItems.length > 1;
        this.querySelectorAll('[data-media-modal-nav]').forEach((button) => {
          button.hidden = !hasMultipleMedia;
          button.disabled = !hasMultipleMedia;
        });
      }

      applyVariantMediaFilter(selectedGroup = '', preferredMediaId = '') {
        const mediaItems = this.allMediaItems;
        if (!mediaItems.length) return;

        const groupedMediaItems = mediaItems.filter((item) => item.dataset.mediaGroup);
        const hasMatchingGroup =
          selectedGroup && groupedMediaItems.some((item) => item.dataset.mediaGroup === selectedGroup);
        const shouldFilter = Boolean(selectedGroup && hasMatchingGroup);

        mediaItems.forEach((item) => {
          const itemGroup = item.dataset.mediaGroup || '';
          const visible = !shouldFilter || itemGroup === selectedGroup;
          item.hidden = !visible;
          if (!visible) item.classList.remove('active');
        });

        const visibleMediaItems = this.mediaItems;
        const preferredMedia = visibleMediaItems.find((item) => item.dataset.mediaId === preferredMediaId);
        const activeMedia = visibleMediaItems.find((item) => item.classList.contains('active'));
        const fallbackMedia = preferredMedia || activeMedia || visibleMediaItems[0];

        if (this.hasAttribute('open') && fallbackMedia) {
          this.showActiveMedia(fallbackMedia.dataset.mediaId);
        } else if (!activeMedia && fallbackMedia) {
          fallbackMedia.classList.add('active');
        }

        this.updateNavigation();
      }

      showRelativeMedia(step) {
        const mediaItems = this.navigationMediaItems;
        if (mediaItems.length < 2) return;

        const activeIndex = mediaItems.findIndex((item) => item.classList.contains('active'));
        const currentIndex = activeIndex >= 0 ? activeIndex : 0;
        const nextIndex = (currentIndex + step + mediaItems.length) % mediaItems.length;
        this.showActiveMedia(mediaItems[nextIndex].dataset.mediaId);
      }

      onModalPointerUpCapture(event) {
        if (event.target.closest('[data-media-modal-nav]')) {
          event.stopPropagation();
          return;
        }

        if (!this.isPopupDesktopMode()) return;
        if (!event.target.closest('.product-media-modal__image')) return;

        event.stopPropagation();
      }

      onNavigationClick(event) {
        const navigationButton = event.target.closest('[data-media-modal-nav]');
        if (!navigationButton) return;

        event.preventDefault();
        event.stopPropagation();
        this.showRelativeMedia(navigationButton.dataset.mediaModalNav === 'next' ? 1 : -1);
      }

      onKeyDown(event) {
        if (!this.hasAttribute('open')) return;
        if (event.target instanceof HTMLElement && event.target.matches('input, textarea, select')) return;

        if (event.code === 'ArrowLeft') {
          event.preventDefault();
          this.showRelativeMedia(-1);
        }

        if (event.code === 'ArrowRight') {
          event.preventDefault();
          this.showRelativeMedia(1);
        }
      }

      onPopupPointerUp(event) {
        const isPopupMode = this.classList.contains('product-media-modal--popup');
        const isMobile = window.matchMedia('(max-width: 749px)').matches;

        if (!isPopupMode || !isMobile || event.pointerType === 'mouse') return;

        if (event.target.closest('.product-media-modal__toggle, [data-media-modal-nav]')) return;

        const isInsideMedia = event.target.closest(
          'deferred-media, product-model, .product-media-modal__model, img[data-media-id]'
        );

        if (isInsideMedia) return;

        event.preventDefault();
        event.stopPropagation();
        this.suppressGhostClick(event);
        this.hide();
      }
    }
  );
}
